/**
 * Normalize collectionCoverage API block into a display-ready view model.
 * Returns null when collectionCoverage is missing (graceful degradation).
 *
 * Expected API shape (from ICR Collection Coverage plan):
 * collectionCoverage: {
 *   linked: { overallScorePct, equivalentFullCount, coveredCount, requirementCount, materialCount, materials[] },
 *   unlinked: { equivalentFullCount, coveredCount, requirementCount, materialCount, materials[] }, // no headline %
 *   fintelUsage: { pctMaterialsCited, citedCount, totalCount, materials[] },
 *   eeis?: [{ eeiId, eeiIndex, linked?, unlinked?, fintelUsage? }],
 *   computing?: boolean,
 * }
 */

function asNumber(value) {
    if (value == null || value === '') {
        return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function asInt(value) {
    const n = asNumber(value);
    return n == null ? null : Math.round(n);
}

function asPct(value) {
    const n = asNumber(value);
    if (n == null) {
        return null;
    }
    return Math.max(0, Math.min(100, Math.round(n)));
}

function materialDisplayId(material) {
    if (!material || typeof material !== 'object') {
        return '';
    }
    const title = material.title || material.reportTitle || null;
    const explicitId = material.cmNumber || material.materialNumber || material.iirNumber || null;
    if (explicitId) {
        return String(explicitId);
    }
    const humanId = material.humanId != null && material.humanId !== ''
        ? String(material.humanId)
        : '';
    // humanId that merely repeats the title is not a useful id (legacy IIR shape).
    if (humanId && humanId !== title) {
        return humanId;
    }
    return String(
        material.reportId
        || material.dataItemId
        || material.materialId
        || material.id
        || humanId
        || title
        || ''
    );
}

function normalizeMaterial(raw, index = 0) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const kind = String(raw.materialKind || raw.kind || '').toLowerCase();
    const cited = raw.cited === true || raw.cited === 'true' || raw.isCited === true;
    const citing = raw.citingFintelIds || raw.citingReportIds || raw.citingFintels || [];
    const citingList = Array.isArray(citing)
        ? citing.map((c) => {
            if (c && typeof c === 'object') {
                return {
                    reportId: String(c.reportId || c.id || ''),
                    title: c.title || c.reportTitle || null,
                };
            }
            return {reportId: String(c), title: null};
        }).filter((c) => c.reportId)
        : [];
    return {
        id: String(raw.materialId || raw.id || materialDisplayId(raw) || `material-${index}`),
        materialKind: kind === 'iir' ? 'iir' : (kind === 'cm' ? 'cm' : (kind || 'unknown')),
        displayId: materialDisplayId(raw) || `Material ${index + 1}`,
        title: raw.title || raw.reportTitle || null,
        collectionTaskId: raw.collectionTaskId || null,
        dataItemId: raw.dataItemId || null,
        reportId: raw.reportId || null,
        author: raw.author || raw.createdBy || null,
        authoredAt: raw.authoredAt || raw.submittedAt || raw.createdAt || null,
        relevancePct: asPct(raw.relevancePct),
        cited,
        citingFintels: citingList,
        coverageDepth: raw.coverageDepth || null,
        factualPct: asNumber(raw.factualPct),
        opinionPct: asNumber(raw.opinionPct),
        explanation: raw.explanation || raw.coverageExplanation || null,
        gapReason: raw.gapReason || null,
    };
}

/** How well a CM/IIR addresses an EEI's CCCs (0–100). */
export function resolveMaterialCccPct(material) {
    if (!material) {
        return null;
    }
    if (material.relevancePct != null) {
        return material.relevancePct;
    }
    const factual = material.factualPct;
    const opinion = material.opinionPct;
    if (factual != null || opinion != null) {
        const sum = (Number(factual) || 0) + (Number(opinion) || 0);
        if (sum > 0) {
            return Math.max(0, Math.min(100, Math.round(sum)));
        }
    }
    return null;
}

const MATERIAL_COVERAGE_DEPTH_PHRASES = {
    substantive: 'Substantively addresses the EEI collection requirements.',
    partial: 'Partially addresses relevant collection requirements.',
    mention: 'Mentions topics related to the EEI requirements.',
    none: 'Does not substantively address the EEI requirements.',
};

function firstSentence(value) {
    if (value == null || value === '') {
        return null;
    }
    const phrase = String(value).replace(/\s+/g, ' ').trim();
    if (!phrase) {
        return null;
    }
    const parts = phrase.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [phrase];
    return parts[0]?.trim() || null;
}

/** Single-sentence rationale for what a material covers toward an EEI. */
export function materialCoverageExplanation(material) {
    if (!material) {
        return null;
    }
    const fromApi = material.explanation || material.gapReason;
    const sentence = firstSentence(fromApi);
    if (sentence) {
        return sentence;
    }
    const depth = String(material.coverageDepth || '').toLowerCase();
    if (MATERIAL_COVERAGE_DEPTH_PHRASES[depth]) {
        return MATERIAL_COVERAGE_DEPTH_PHRASES[depth];
    }
    const pct = resolveMaterialCccPct(material);
    if (pct != null && pct >= 90) {
        return MATERIAL_COVERAGE_DEPTH_PHRASES.substantive;
    }
    if (pct != null && pct >= 35) {
        return MATERIAL_COVERAGE_DEPTH_PHRASES.partial;
    }
    if (pct != null && pct >= 10) {
        return MATERIAL_COVERAGE_DEPTH_PHRASES.mention;
    }
    if (pct != null && pct > 0) {
        return 'Provides limited coverage of the EEI requirements.';
    }
    return null;
}

/** Single-sentence rationale for what is covered toward an EEI (linked + suggested pools). */
export function eeiCollectionExplanation(row) {
    if (!row) {
        return null;
    }
    const fromApi = firstSentence(row.explanation || row.gapReason);
    if (fromApi) {
        return fromApi;
    }
    for (const material of [...(row.linkedMaterials || []), ...(row.unlinkedMaterials || [])]) {
        const explanation = materialCoverageExplanation(material);
        if (explanation) {
            return explanation;
        }
    }
    const linkedDepth = String(row.linkedCoverageDepth || '').toLowerCase();
    if (MATERIAL_COVERAGE_DEPTH_PHRASES[linkedDepth]) {
        return MATERIAL_COVERAGE_DEPTH_PHRASES[linkedDepth];
    }
    const unlinkedDepth = String(row.unlinkedCoverageDepth || '').toLowerCase();
    if (MATERIAL_COVERAGE_DEPTH_PHRASES[unlinkedDepth]) {
        return MATERIAL_COVERAGE_DEPTH_PHRASES[unlinkedDepth];
    }
    const pct = row.generalScorePct ?? row.linkedScorePct;
    if (row.status === COLLECTION_EEI_STATUS.COVERED) {
        return 'Linked materials substantively address the EEI collection requirements.';
    }
    if (row.status === COLLECTION_EEI_STATUS.PARTIAL) {
        return pct != null
            ? `Materials partially address the EEI requirements (${Math.round(pct)}% coverage).`
            : 'Materials partially address the EEI collection requirements.';
    }
    return 'No linked materials substantively address the EEI requirements.';
}

function normalizeMaterials(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map(normalizeMaterial).filter(Boolean);
}

function normalizeLinked(linked) {
    if (!linked || typeof linked !== 'object') {
        return null;
    }
    const materials = normalizeMaterials(linked.materials);
    return {
        overallScorePct: asPct(
            linked.overallScorePct ?? linked.weightedCoveragePct ?? linked.overallScore,
        ),
        equivalentFullCount: asInt(linked.equivalentFullCount),
        coveredCount: asInt(linked.coveredCount),
        requirementCount: asInt(linked.requirementCount),
        materialCount: asInt(linked.materialCount) ?? materials.length,
        materials,
    };
}

function normalizeUnlinked(unlinked) {
    if (!unlinked || typeof unlinked !== 'object') {
        return null;
    }
    const materials = normalizeMaterials(unlinked.materials);
    return {
        // Intentionally no headline % — absolute metrics only
        equivalentFullCount: asInt(unlinked.equivalentFullCount),
        coveredCount: asInt(unlinked.coveredCount),
        requirementCount: asInt(unlinked.requirementCount),
        materialCount: asInt(unlinked.materialCount) ?? materials.length,
        materials,
    };
}

function normalizeFintelUsage(usage) {
    if (!usage || typeof usage !== 'object') {
        return null;
    }
    const materials = normalizeMaterials(usage.materials);
    const citedCount = asInt(usage.citedCount);
    const totalCount = asInt(usage.totalCount) ?? materials.length;
    let pct = asPct(usage.pctMaterialsCited ?? usage.overallScorePct);
    if (pct == null && citedCount != null && totalCount > 0) {
        pct = Math.round((100 * citedCount) / totalCount);
    }
    return {
        pctMaterialsCited: pct,
        citedCount,
        totalCount,
        materials,
    };
}

/** Discrete collection coverage band per EEI (linked pool score). */
export const COLLECTION_EEI_STATUS = {
    COVERED: 'covered',
    PARTIAL: 'partial',
    UNCOVERED: 'uncovered',
};

export const COLLECTION_EEI_STATUS_COLORS = {
    [COLLECTION_EEI_STATUS.COVERED]: '#16A34A',
    [COLLECTION_EEI_STATUS.PARTIAL]: '#CA8A04',
    [COLLECTION_EEI_STATUS.UNCOVERED]: '#DC2626',
};

export const COLLECTION_EEI_STATUS_LABELS = {
    [COLLECTION_EEI_STATUS.COVERED]: 'Covered',
    [COLLECTION_EEI_STATUS.PARTIAL]: 'Partially covered',
    [COLLECTION_EEI_STATUS.UNCOVERED]: 'Not covered',
};

/**
 * Map linked collection score (0–100) to covered / partial / uncovered.
 * Thresholds match analytic {@link computePirStatus}: ≥90 covered, 10–89 partial, <10 uncovered.
 */
export function resolveCollectionEeiStatus(linkedScorePct, coverageDepth = null) {
    if (linkedScorePct != null) {
        const score = Number(linkedScorePct);
        if (!Number.isFinite(score) || score < 10) {
            return COLLECTION_EEI_STATUS.UNCOVERED;
        }
        if (score >= 90) {
            return COLLECTION_EEI_STATUS.COVERED;
        }
        return COLLECTION_EEI_STATUS.PARTIAL;
    }
    const depth = String(coverageDepth || 'none').toLowerCase();
    if (depth === 'substantive' || depth === 'full' || depth === 'deep') {
        return COLLECTION_EEI_STATUS.COVERED;
    }
    if (depth === 'partial' || depth === 'mention' || depth === 'shallow') {
        return COLLECTION_EEI_STATUS.PARTIAL;
    }
    return COLLECTION_EEI_STATUS.UNCOVERED;
}

function normalizeRequirement(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const linkedScorePct = asPct(raw.linkedScore ?? raw.linkedScorePct);
    const unlinkedScorePct = asPct(raw.unlinkedScore ?? raw.unlinkedScorePct);
    return {
        requirementId: raw.requirementId || raw.id || null,
        text: raw.text || null,
        eeiIndex: asInt(raw.eeiIndex),
        evaluatorKind: raw.evaluatorKind || null,
        countsTowardScalar: raw.countsTowardScalar !== false,
        linkedScorePct,
        linkedCoverageDepth: raw.linkedCoverageDepth || null,
        unlinkedScorePct,
        unlinkedCoverageDepth: raw.unlinkedCoverageDepth || null,
        generalScorePct: resolveGeneralEeiScore(linkedScorePct, unlinkedScorePct),
        linkedSupportingMaterialIds: normalizeIdList(
            raw.linkedSupportingMaterialIds || raw.linkedSupportingMaterials,
        ),
        unlinkedSupportingMaterialIds: normalizeIdList(
            raw.unlinkedSupportingMaterialIds || raw.unlinkedSupportingMaterials,
        ),
        unlinkedRelevancePcts: normalizeRelevanceMap(raw.unlinkedMaterialRelevancePcts),
        gapReason: raw.gapReason || null,
        explanation: raw.explanation || raw.coverageExplanation || null,
    };
}

/** {materialId: pct} — per-EEI relevance of suggested (unlinked) materials. */
function normalizeRelevanceMap(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return {};
    }
    const out = {};
    for (const [materialId, pct] of Object.entries(raw)) {
        const normalized = asPct(pct);
        if (normalized != null) {
            out[String(materialId)] = normalized;
        }
    }
    return out;
}

function normalizeIdList(value) {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.map((id) => String(id)).filter(Boolean);
}

/**
 * General EEI coverage score from linked and unlinked pools (best of either, capped at 100).
 */
export function resolveGeneralEeiScore(linkedScorePct, unlinkedScorePct) {
    const linked = linkedScorePct != null ? Number(linkedScorePct) : null;
    const unlinked = unlinkedScorePct != null ? Number(unlinkedScorePct) : null;
    if (linked == null && unlinked == null) {
        return null;
    }
    if (linked == null) {
        return Math.max(0, Math.min(100, Math.round(unlinked)));
    }
    if (unlinked == null) {
        return Math.max(0, Math.min(100, Math.round(linked)));
    }
    return Math.max(0, Math.min(100, Math.round(Math.max(linked, unlinked))));
}

function resolveGeneralCoverageDepth(linkedDepth, unlinkedDepth, generalScorePct) {
    if (generalScorePct != null) {
        return resolveCollectionEeiStatus(generalScorePct);
    }
    const linkedStatus = resolveCollectionEeiStatus(null, linkedDepth);
    const unlinkedStatus = resolveCollectionEeiStatus(null, unlinkedDepth);
    if (linkedStatus === COLLECTION_EEI_STATUS.COVERED || unlinkedStatus === COLLECTION_EEI_STATUS.COVERED) {
        return COLLECTION_EEI_STATUS.COVERED;
    }
    if (linkedStatus === COLLECTION_EEI_STATUS.PARTIAL || unlinkedStatus === COLLECTION_EEI_STATUS.PARTIAL) {
        return COLLECTION_EEI_STATUS.PARTIAL;
    }
    return COLLECTION_EEI_STATUS.UNCOVERED;
}

function indexMaterialsById(view) {
    const map = new Map();
    for (const material of view?.linked?.materials || []) {
        map.set(material.id, material);
    }
    for (const material of view?.unlinked?.materials || []) {
        if (!map.has(material.id)) {
            map.set(material.id, material);
        }
    }
    for (const material of view?.fintelUsage?.materials || []) {
        if (!map.has(material.id)) {
            map.set(material.id, material);
        }
    }
    return map;
}

function materialsForIds(materialIndex, ids = []) {
    if (!ids?.length) {
        return [];
    }
    const out = [];
    const seen = new Set();
    for (const id of ids) {
        const key = String(id);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        const material = materialIndex.get(key);
        if (material) {
            out.push(material);
        }
    }
    return out;
}

function matchRequirementForEei(requirements, {eeiId, eeiNumber, eeiText, eeiIndex} = {}) {
    if (!Array.isArray(requirements) || requirements.length === 0) {
        return null;
    }
    const pool = Array.isArray(requirements) ? requirements : [];
    const needle = eeiText
        ? String(eeiText).trim().toLowerCase().replace(/\s+/g, ' ')
        : null;

    let best = null;
    let bestRank = Number.MAX_SAFE_INTEGER;
    for (const req of pool) {
        let rank = Number.MAX_SAFE_INTEGER;
        const reqText = req.text
            ? String(req.text).trim().toLowerCase().replace(/\s+/g, ' ')
            : null;
        if (needle && reqText && needle === reqText) {
            rank = 0;
        } else if (eeiIndex != null && req.eeiIndex != null && Number(req.eeiIndex) === Number(eeiIndex)) {
            rank = 1;
        }
        if (rank < bestRank) {
            bestRank = rank;
            best = req;
        }
    }
    return bestRank === Number.MAX_SAFE_INTEGER ? null : best;
}

function mergeMaterialsById(...lists) {
    const map = new Map();
    for (const list of lists) {
        for (const material of list || []) {
            if (!material?.id) {
                continue;
            }
            const existing = map.get(material.id);
            if (!existing) {
                map.set(material.id, material);
                continue;
            }
            // Prefer the copy that carries relevance / richer metadata.
            if (existing.relevancePct == null && material.relevancePct != null) {
                map.set(material.id, {...existing, ...material});
            } else if (!existing.title && material.title) {
                map.set(material.id, {...existing, ...material});
            }
        }
    }
    return [...map.values()];
}

function buildEeiCoverageEntry({
    eei,
    eeiIndex,
    collectionView,
    requirements,
    materialIndex,
}) {
    const collectionRow = findCollectionEeiRow(collectionView, {
        eeiId: eei?.id,
        eeiNumber: eei?.eeiNumber,
        eeiIndex,
    });
    const matchedReq = matchRequirementForEei(requirements, {
        eeiId: eei?.id,
        eeiNumber: eei?.eeiNumber,
        eeiText: eei?.text,
        eeiIndex,
    });
    const linkedScorePct = collectionRow?.linkedScorePct
        ?? collectionRow?.coveragePct
        ?? collectionRow?.linked?.overallScorePct
        ?? matchedReq?.linkedScorePct
        ?? null;
    const unlinkedScorePct = matchedReq?.unlinkedScorePct ?? null;
    const generalScorePct = asPct(collectionRow?.coveragePct)
        ?? resolveGeneralEeiScore(linkedScorePct, unlinkedScorePct);
    const status = resolveGeneralCoverageDepth(
        matchedReq?.linkedCoverageDepth,
        matchedReq?.unlinkedCoverageDepth,
        generalScorePct,
    );
    const relevancePcts = matchedReq?.unlinkedRelevancePcts || {};
    const linkedMaterials = mergeMaterialsById(
        materialsForIds(materialIndex, matchedReq?.linkedSupportingMaterialIds),
        collectionRow?.linked?.materials,
    );
    const unlinkedMaterials = mergeMaterialsById(
        materialsForIds(materialIndex, matchedReq?.unlinkedSupportingMaterialIds)
            .map((material) => ({
                ...material,
                relevancePct: relevancePcts[material.id] ?? material.relevancePct ?? null,
            })),
        collectionRow?.unlinked?.materials,
    );
    const row = {
        eeiId: eei?.id || collectionRow?.eeiId || null,
        eeiNumber: eei?.eeiNumber || collectionRow?.eeiNumber || null,
        eeiIndex,
        text: eei?.text || matchedReq?.text || collectionRow?.text || null,
        linkedScorePct,
        unlinkedScorePct,
        generalScorePct,
        status,
        label: eei?.eeiNumber || eei?.id || `EEI ${eeiIndex}`,
        linkedCoverageDepth: matchedReq?.linkedCoverageDepth ?? null,
        unlinkedCoverageDepth: matchedReq?.unlinkedCoverageDepth ?? null,
        gapReason: matchedReq?.gapReason ?? null,
        explanation: matchedReq?.explanation ?? null,
        linkedMaterials,
        unlinkedMaterials,
    };
    return {
        ...row,
        explanation: row.explanation || eeiCollectionExplanation(row),
    };
}

/**
 * One segment per EEI for ICR collection coverage progress bars.
 * @param {object|null} collectionView Output of buildCollectionCoverageView
 * @param {Array<{id?, eeiNumber?, text?}>} [pirEeis] EEIs from resolvePirEeis(icr)
 * @returns {Array<{eeiId, eeiNumber, eeiIndex, generalScorePct, linkedScorePct, status, label}>}
 */
export function buildCollectionEeiSegments(collectionView, pirEeis = null) {
    if (!collectionView) {
        return [];
    }
    const requirements = collectionView.requirements || [];
    const materialIndex = indexMaterialsById(collectionView);
    const eeis = Array.isArray(pirEeis) && pirEeis.length > 0
        ? pirEeis
        : (collectionView.eeis || []).map((row) => ({
            id: row.eeiId,
            eeiNumber: row.eeiNumber,
            text: row.text,
        }));

    if (!eeis.length) {
        const byIndex = new Map();
        for (const req of requirements) {
            if (req?.eeiIndex == null || req.evaluatorKind !== 'SUBSTANTIVE') {
                continue;
            }
            const idx = Number(req.eeiIndex);
            const existing = byIndex.get(idx);
            const reqScore = req.generalScorePct ?? req.linkedScorePct ?? -1;
            const existingScore = existing?.generalScorePct ?? existing?.linkedScorePct ?? -1;
            if (!existing || reqScore > existingScore) {
                byIndex.set(idx, req);
            }
        }
        const indices = [...byIndex.keys()].sort((a, b) => a - b);
        if (indices.length === 0) {
            return [];
        }
        return indices.map((eeiIndex) => {
            const req = byIndex.get(eeiIndex);
            const generalScorePct = req?.generalScorePct
                ?? resolveGeneralEeiScore(req?.linkedScorePct, req?.unlinkedScorePct);
            return {
                eeiId: null,
                eeiNumber: null,
                eeiIndex,
                linkedScorePct: req?.linkedScorePct ?? null,
                generalScorePct,
                status: resolveGeneralCoverageDepth(
                    req?.linkedCoverageDepth,
                    req?.unlinkedCoverageDepth,
                    generalScorePct,
                ),
                label: `EEI ${eeiIndex}`,
            };
        });
    }

    return eeis.map((eei, index) => {
        const entry = buildEeiCoverageEntry({
            eei,
            eeiIndex: index + 1,
            collectionView,
            requirements,
            materialIndex,
        });
        return {
            eeiId: entry.eeiId,
            eeiNumber: entry.eeiNumber,
            eeiIndex: entry.eeiIndex,
            linkedScorePct: entry.linkedScorePct,
            generalScorePct: entry.generalScorePct,
            status: entry.status,
            label: entry.label,
        };
    });
}

/**
 * Per-EEI rows for the collection coverage EEIs tab.
 */
export function buildCollectionEeiRows(collectionView, pirEeis = null) {
    if (!collectionView) {
        return [];
    }
    const requirements = collectionView.requirements || [];
    const materialIndex = indexMaterialsById(collectionView);
    const eeis = Array.isArray(pirEeis) && pirEeis.length > 0
        ? pirEeis
        : (collectionView.eeis || []).map((row) => ({
            id: row.eeiId,
            eeiNumber: row.eeiNumber,
            text: row.text,
        }));

    if (!eeis.length) {
        return buildCollectionEeiSegments(collectionView, pirEeis).map((segment) => ({
            ...segment,
            text: null,
            linkedMaterials: [],
            unlinkedMaterials: [],
        }));
    }

    return eeis.map((eei, index) => buildEeiCoverageEntry({
        eei,
        eeiIndex: index + 1,
        collectionView,
        requirements,
        materialIndex,
    }));
}

/**
 * FINTEL-centric citation view: each FINTEL with cited CMs/IIRs.
 */
export function buildFintelUsageByReport(view) {
    if (!view?.fintelUsage) {
        return [];
    }
    const byReport = new Map();
    for (const material of view.fintelUsage.materials || []) {
        if (!material.cited) {
            continue;
        }
        for (const fintel of material.citingFintels || []) {
            const reportId = fintel.reportId;
            if (!reportId) {
                continue;
            }
            if (!byReport.has(reportId)) {
                byReport.set(reportId, {
                    reportId,
                    title: fintel.title || null,
                    materials: [],
                });
            }
            const row = byReport.get(reportId);
            if (!row.title && fintel.title) {
                row.title = fintel.title;
            }
            if (!row.materials.some((m) => m.id === material.id)) {
                row.materials.push(material);
            }
        }
    }
    return [...byReport.values()].sort((a, b) => {
        const titleA = (a.title || a.reportId || '').toLowerCase();
        const titleB = (b.title || b.reportId || '').toLowerCase();
        if (titleA !== titleB) {
            return titleA.localeCompare(titleB);
        }
        return String(a.reportId).localeCompare(String(b.reportId));
    });
}

/**
 * List-page / summary coverage may include a precomputed collectionEeis array.
 */
export function resolveCollectionEeiSegments(coverageOrSummary) {
    if (!coverageOrSummary || typeof coverageOrSummary !== 'object') {
        return [];
    }
    const raw = coverageOrSummary.collectionEeis;
    if (!Array.isArray(raw) || raw.length === 0) {
        return [];
    }
    return raw.map((row, index) => {
        const linkedScorePct = asPct(row.linkedScorePct ?? row.linkedScore);
        const status = resolveCollectionEeiStatus(linkedScorePct, row.linkedCoverageDepth ?? row.coverageDepth);
        return {
            eeiId: row.eeiId || row.id || null,
            eeiNumber: row.eeiNumber || null,
            eeiIndex: asInt(row.eeiIndex) ?? (index + 1),
            linkedScorePct,
            status,
            label: row.eeiNumber || row.eeiId || `EEI ${asInt(row.eeiIndex) ?? (index + 1)}`,
        };
    });
}

function normalizeEeiRow(raw, index = 0) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    return {
        eeiId: raw.eeiId || raw.id || null,
        eeiIndex: asInt(raw.eeiIndex) ?? (index + 1),
        eeiNumber: raw.eeiNumber || null,
        text: raw.text || raw.question || null,
        linked: normalizeLinked(raw.linked || raw.collectionLinked),
        unlinked: normalizeUnlinked(raw.unlinked || raw.collectionUnlinked),
        fintelUsage: normalizeFintelUsage(raw.fintelUsage || raw.collectionFintelUsage),
        coveragePct: asPct(raw.coveragePct ?? raw.coverage_pct),
        // Flat fallbacks when backend nests metrics shallowly
        linkedScorePct: asPct(
            raw.coveragePct
            ?? raw.linkedScorePct
            ?? raw.collectionLinkedScorePct
            ?? raw.linked?.overallScorePct
            ?? raw.overallScorePct,
        ),
        cited: raw.cited === true || raw.cited === 'true' || raw.isCited === true,
    };
}

/**
 * ICR-level suggested (unlinked) materials not already listed under any EEI row.
 */
export function icrWideSuggestedMaterials(view, eeiRows = []) {
    const pool = view?.unlinked?.materials || [];
    if (!pool.length) {
        return [];
    }
    const shown = new Set();
    for (const row of eeiRows || []) {
        for (const material of row.linkedMaterials || []) {
            if (material?.id) shown.add(String(material.id));
        }
        for (const material of row.unlinkedMaterials || []) {
            if (material?.id) shown.add(String(material.id));
        }
    }
    return pool.filter((material) => material?.id && !shown.has(String(material.id)));
}

/**
 * Score for one EEI segment when computing overall collection coverage.
 */
function segmentScoreForOverall(segment) {
    if (segment?.generalScorePct != null) {
        return segment.generalScorePct;
    }
    if (segment?.linkedScorePct != null) {
        return segment.linkedScorePct;
    }
    if (segment?.status === COLLECTION_EEI_STATUS.COVERED) {
        return 95;
    }
    if (segment?.status === COLLECTION_EEI_STATUS.PARTIAL) {
        return 50;
    }
    if (segment?.status === COLLECTION_EEI_STATUS.UNCOVERED) {
        return 0;
    }
    return null;
}

/**
 * Overall collection coverage = average general score across EEIs (equal weight per EEI).
 */
export function computeGeneralCollectionPct(segments) {
    if (!Array.isArray(segments) || segments.length === 0) {
        return null;
    }
    const scores = segments.map(segmentScoreForOverall).filter((s) => s != null);
    if (scores.length === 0) {
        return null;
    }
    return Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length);
}

/**
 * ICR-level Collection Coverage % for the RHS card and modal Overview ring.
 * Always prefer CCC v2 headline so both surfaces stay in lockstep.
 */
export function resolveIcrCollectionHeadlinePct(view) {
    if (!view || typeof view !== 'object') {
        return null;
    }
    return asPct(view.headlinePct)
        ?? asPct(view.generalScorePct)
        ?? asPct(view.linkedScorePct)
        ?? null;
}

/**
 * Resolve headline collection coverage % from a snapshot or list summary.
 * Prefers CCC nest headline / overall, then EEI-segment average, then linked pool %.
 * Does not read root headlinePct/overallPct — those are analytic on GET /coverage.
 */
export function resolveGeneralCollectionPct(coverageOrSummary, pirEeis = null) {
    if (!coverageOrSummary || typeof coverageOrSummary !== 'object') {
        return null;
    }
    const nested = coverageOrSummary.collectionCoverage;
    // Full GET /coverage: top-level headlinePct is *analytic*. Collection
    // headline (modal Data ring) lives on collectionCoverage. Only treat
    // top-level headline as collection when this object has no nest (list/summary).
    const v2Headline = asPct(
        nested?.headlinePct
        ?? nested?.overallScorePct
        ?? (nested == null ? coverageOrSummary.headlinePct : null)
        ?? (nested == null ? coverageOrSummary.overallScorePct : null),
    );
    if (v2Headline != null) {
        return v2Headline;
    }
    const view = buildCollectionCoverageView(coverageOrSummary);
    if (view) {
        if (view.headlinePct != null) {
            return view.headlinePct;
        }
        const segments = buildCollectionEeiSegments(view, pirEeis);
        const generalPct = computeGeneralCollectionPct(segments);
        if (generalPct != null) {
            return generalPct;
        }
        return view.linkedScorePct ?? null;
    }
    const listSegments = resolveCollectionEeiSegments(coverageOrSummary);
    const fromList = computeGeneralCollectionPct(listSegments);
    if (fromList != null) {
        return fromList;
    }
    return asPct(
        coverageOrSummary.dataCoveragePct
        ?? coverageOrSummary.collectionOverallScorePct
        ?? coverageOrSummary.collectionLinkedScorePct,
    ) ?? resolveCollectionLinkedPct(coverageOrSummary);
}

/**
 * Resolve collection linked % from a list/summary coverage object.
 * Prefers collection fields; returns null when absent (caller may fall back to analytic).
 */
export function resolveCollectionLinkedPct(coverageOrSummary) {
    if (!coverageOrSummary || typeof coverageOrSummary !== 'object') {
        return null;
    }
    const nested = coverageOrSummary.collectionCoverage?.linked;
    return asPct(
        coverageOrSummary.collectionOverallScorePct
        ?? coverageOrSummary.collectionLinkedScorePct
        ?? nested?.overallScorePct
        ?? nested?.weightedCoveragePct
        ?? nested?.overallScore
        ?? coverageOrSummary.collectionCoverage?.overallScorePct,
    );
}

/**
 * Resolve analytic overall % from a list/summary coverage object.
 * Prefers headline when present (same as modal / Coverage tab).
 */
export function resolveAnalyticOverallPct(coverageOrSummary) {
    if (!coverageOrSummary || typeof coverageOrSummary !== 'object') {
        return null;
    }
    const nest = coverageOrSummary.analyticCoverage;
    return asPct(
        nest?.headlinePct
        ?? nest?.overallPct
        ?? coverageOrSummary.analyticOverallScorePct
        ?? coverageOrSummary.headlinePct
        ?? coverageOrSummary.overallPct
        ?? coverageOrSummary.overallScore
        ?? nest?.overallScore
        ?? coverageOrSummary.analyticCoverage?.overallScore
        ?? coverageOrSummary.analyticCoveragePct,
    );
}

/**
 * Per-EEI collection metrics from an EEI `coverage` payload (deriveCoverageSummary extension).
 * Authoritative nest: coverage.collection { linkedScorePct, unlinked* absolutes, cited }.
 * Legacy/mistaken nest collectionCoverage is still accepted.
 * Returns null when no collection fields are present.
 */
export function resolveEeiCollectionSummary(coverage) {
    if (!coverage || typeof coverage !== 'object') {
        return null;
    }

    // Backend EeiService.deriveCoverageSummary → coverage.collection
    const collection = coverage.collection;
    if (collection && typeof collection === 'object') {
        const fromFlat = resolveFlatCollectionSummary(collection);
        if (fromFlat) {
            return fromFlat;
        }
    }

    // Mistaken / older nest: coverage.collectionCoverage (full snapshot block)
    const nested = coverage.collectionCoverage;
    if (nested && typeof nested === 'object') {
        // Flat EEI-style fields accidentally placed under collectionCoverage
        const fromNestedFlat = resolveFlatCollectionSummary(nested);
        if (fromNestedFlat) {
            return fromNestedFlat;
        }
        const view = buildCollectionCoverageView({collectionCoverage: nested});
        if (view) {
            return {
                linkedScorePct: view.linked?.overallScorePct ?? view.linkedScorePct ?? null,
                unlinked: view.unlinked,
                fintelUsage: view.fintelUsage,
                cited: nested.cited === true || nested.cited === false
                    ? nested.cited === true
                    : (view.fintelUsage?.pctMaterialsCited != null
                        ? (view.fintelUsage.pctMaterialsCited > 0)
                        : null),
            };
        }
    }

    return resolveFlatCollectionSummary(coverage);
}

/**
 * Normalize a flat collection summary object (EEI coverage.collection shape).
 */
function resolveFlatCollectionSummary(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const linkedScorePct = asPct(
        raw.linkedScorePct
        ?? raw.linkedOverallScorePct
        ?? raw.collectionOverallScorePct
        ?? raw.collectionLinkedScorePct
        ?? raw.linked?.overallScorePct,
    );
    const unlinkedRaw = raw.unlinked || (
        raw.unlinkedEquivalentFullCount != null
        || raw.unlinkedCoveredCount != null
        || raw.unlinkedRequirementCount != null
            ? {
                equivalentFullCount: raw.unlinkedEquivalentFullCount,
                coveredCount: raw.unlinkedCoveredCount,
                requirementCount: raw.unlinkedRequirementCount,
            }
            : raw.collectionUnlinked
    );
    const unlinked = normalizeUnlinked(unlinkedRaw);
    const unlinkedPresent = unlinked != null && (
        unlinked.equivalentFullCount != null
        || unlinked.coveredCount != null
        || unlinked.requirementCount != null
        || (unlinked.materials && unlinked.materials.length > 0)
    );
    const fintelUsage = normalizeFintelUsage(
        raw.fintelUsage
        || raw.collectionFintelUsage
        || ((raw.pctMaterialsCited != null || raw.citedCount != null)
            ? {
                pctMaterialsCited: raw.pctMaterialsCited,
                citedCount: raw.citedCount,
                totalCount: raw.totalLinkedMaterials ?? raw.totalCount,
            }
            : null),
    );
    const citedExplicit = raw.cited === true || raw.cited === false
        || raw.cited === 'true' || raw.isCited === true;
    const cited = raw.cited === true || raw.cited === 'true' || raw.isCited === true
        || (fintelUsage?.citedCount != null && fintelUsage.citedCount > 0);
    const hasAny = linkedScorePct != null || unlinkedPresent || fintelUsage != null || citedExplicit;
    if (!hasAny) {
        return null;
    }
    return {
        linkedScorePct,
        unlinked: unlinkedPresent ? unlinked : null,
        fintelUsage,
        cited: citedExplicit
            ? (raw.cited === true || raw.cited === 'true' || raw.isCited === true)
            : (fintelUsage?.citedCount != null ? cited : null),
    };
}

/**
 * @param {object|null} snapshot Full coverage GET response (analytic + optional collectionCoverage)
 * @returns {object|null}
 */
export function buildCollectionCoverageView(snapshot) {
    const raw = snapshot?.collectionCoverage;
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const linked = normalizeLinked(raw.linked);
    const unlinked = normalizeUnlinked(raw.unlinked);
    const fintelUsage = normalizeFintelUsage(raw.fintelUsage);
    const eeis = Array.isArray(raw.eeis)
        ? raw.eeis.map(normalizeEeiRow).filter(Boolean)
        : [];
    const requirements = Array.isArray(raw.requirements)
        ? raw.requirements.map(normalizeRequirement).filter(Boolean)
        : [];

    const hasAny = linked != null || unlinked != null || fintelUsage != null || eeis.length > 0
        || requirements.length > 0
        || raw.computing === true;
    if (!hasAny) {
        return null;
    }

    const asPct = (value) => {
        if (value == null || value === '') {
            return null;
        }
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return null;
        }
        return Math.max(0, Math.min(100, Math.round(n)));
    };
    const asCount = (value) => {
        if (value == null || value === '') {
            return null;
        }
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    };

    const linkedScorePct = linked?.overallScorePct ?? null;
    const generalScorePct = computeGeneralCollectionPct(
        buildCollectionEeiSegments({
            computing: Boolean(raw.computing),
            linked,
            unlinked,
            fintelUsage,
            eeis,
            requirements,
        }),
    );
    // Headline / linked-only / discovered % only when the ACC/CCC snapshot exposes them.
    const headlinePct = asPct(raw.headlinePct ?? raw.overallScorePct ?? raw.overall_score_pct);
    const linkedOnlyPct = asPct(raw.linkedOnlyPct ?? raw.linked_only_pct);
    const discoveredPct = asPct(raw.discoveredPct ?? raw.discovered_pct);
    const discoveredCount = asCount(
        raw.discoveredMaterialCount
        ?? raw.discovered_material_count
        ?? raw.discoveredCount,
    );

    return {
        computing: Boolean(raw.computing),
        progressLabel: typeof raw.progressLabel === 'string' ? raw.progressLabel : null,
        progressPhase: typeof raw.progressPhase === 'string' ? raw.progressPhase : null,
        progressStepNumber: asCount(raw.progressStepNumber),
        progressStepTitle: typeof raw.progressStepTitle === 'string' ? raw.progressStepTitle : null,
        progressDetail: typeof raw.progressDetail === 'string' ? raw.progressDetail : null,
        progressCompleted: asCount(raw.progressCompleted),
        progressTotal: asCount(raw.progressTotal),
        linked,
        unlinked,
        fintelUsage,
        eeis,
        requirements,
        linkedScorePct,
        generalScorePct,
        headlinePct,
        linkedOnlyPct,
        discoveredPct,
        discoveredCount,
        usagePct: fintelUsage?.pctMaterialsCited ?? null,
        materialCounts: {
            linked: linked?.materialCount ?? 0,
            unlinked: unlinked?.materialCount ?? 0,
            usage: fintelUsage?.totalCount ?? 0,
            cited: fintelUsage?.citedCount ?? 0,
        },
    };
}

/**
 * Find per-EEI collection row by id / number / 1-based index.
 */
export function findCollectionEeiRow(view, {eeiId, eeiNumber, eeiIndex} = {}) {
    if (!view?.eeis?.length) {
        return null;
    }
    if (eeiId) {
        const byId = view.eeis.find((e) => String(e.eeiId) === String(eeiId));
        if (byId) return byId;
    }
    if (eeiNumber) {
        const byNum = view.eeis.find((e) => String(e.eeiNumber) === String(eeiNumber));
        if (byNum) return byNum;
    }
    if (eeiIndex != null) {
        const idx = Number(eeiIndex);
        const byIndex = view.eeis.find((e) => Number(e.eeiIndex) === idx);
        if (byIndex) return byIndex;
    }
    return null;
}

/**
 * "4/10 EEIs fully covered, 3/10 EEIs partially covered" — from EEI segments.
 */
export function collectionCountsText(segments) {
    if (!Array.isArray(segments) || segments.length === 0) {
        return '';
    }
    const total = segments.length;
    const fully = segments.filter((s) => s.status === COLLECTION_EEI_STATUS.COVERED).length;
    const partially = segments.filter((s) => s.status === COLLECTION_EEI_STATUS.PARTIAL).length;
    return `${fully}/${total} EEIs fully covered, ${partially}/${total} EEIs partially covered`;
}

/**
 * Citations grouped by the cited CM/IIR (owner decision): one group per cited
 * material with the citing FINTELs listed under it.
 * @returns {Array<{material, citingFintels: Array<{reportId, title}>}>}
 */
export function buildCitationsByMaterial(view) {
    if (!view?.fintelUsage) {
        return [];
    }
    return (view.fintelUsage.materials || [])
        .filter((material) => material.cited && (material.citingFintels || []).length > 0)
        .map((material) => ({
            material,
            citingFintels: material.citingFintels,
        }));
}

/** "5 collected materials and 2 IIRs cited" */
export function citationsCountsText(view) {
    const groups = buildCitationsByMaterial(view);
    const cmCount = groups.filter((g) => g.material.materialKind !== 'iir').length;
    const iirCount = groups.filter((g) => g.material.materialKind === 'iir').length;
    const cmPart = `${cmCount} collected material${cmCount === 1 ? '' : 's'}`;
    const iirPart = `${iirCount} IIR${iirCount === 1 ? '' : 's'}`;
    return `${cmPart} and ${iirPart} cited`;
}

export function collectionTabCounts(view, pirEeis = null) {
    if (!view) {
        return {linked: 0, unlinked: 0, fintelCitations: 0, eeis: 0, fintels: 0, citations: 0};
    }
    const eeiRows = buildCollectionEeiRows(view, pirEeis);
    return {
        linked: view.linked?.materialCount ?? view.linked?.materials?.length ?? 0,
        unlinked: view.unlinked?.materialCount ?? view.unlinked?.materials?.length ?? 0,
        fintelCitations: view.fintelUsage?.citedCount ?? 0,
        eeis: eeiRows.length || view.eeis?.length || view.requirements?.length || 0,
        fintels: buildFintelUsageByReport(view).length,
        citations: buildCitationsByMaterial(view).length,
    };
}
